import React, { useMemo, useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarRange } from "lucide-react";

/**
 * Date-range presets supported on every chart.
 * Each preset returns an inclusive [startYM, endYM] tuple where YM is "YYYY-MM",
 * or [null, null] for "all".
 */
export const RANGE_PRESETS = {
  all:           { label: "All time",        compute: () => [null, null] },
  current:       { label: "Current month",   compute: (now) => [ym(now), ym(now)] },
  previous:      { label: "Previous month",  compute: (now) => {
    const d = addMonths(now, -1); const s = ym(d); return [s, s];
  } },
  last3:         { label: "Last 3 months",   compute: (now) => [ym(addMonths(now, -2)), ym(now)] },
  last6:         { label: "Last 6 months",   compute: (now) => [ym(addMonths(now, -5)), ym(now)] },
  last12:        { label: "Last 12 months",  compute: (now) => [ym(addMonths(now, -11)), ym(now)] },
  ytd:           { label: "Year-to-date",    compute: (now) => [`${now.getFullYear()}-01`, ym(now)] },
  prev_year:     { label: "Previous year",   compute: (now) => {
    const y = now.getFullYear() - 1; return [`${y}-01`, `${y}-12`];
  } },
  // `custom` is a sentinel — the actual bounds come from customStart / customEnd
  // state in useChartDateRange. compute() is never called for this key.
  custom:        { label: "Custom range…",   compute: () => [null, null] },
};

function ym(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function addMonths(d, n) {
  const r = new Date(d.getFullYear(), d.getMonth() + n, 1);
  return r;
}

/**
 * useChartDateRange — controls the state of a chart's date range picker.
 *
 *   defaultPreset – key from RANGE_PRESETS; default "all"
 *
 * Returns:
 *   - preset, setPreset
 *   - customStart / customEnd / setCustomStart / setCustomEnd ("YYYY-MM")
 *   - range: { startYM, endYM } current concrete bounds
 *   - inRange(monthStr): boolean
 *   - filter(rows, monthKey="month"): rows[]
 */
export function useChartDateRange(defaultPreset = "all") {
  const [preset, _setPreset] = useState(defaultPreset);
  // Default custom window = "this year so far" until the user adjusts it.
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-01`;
  });
  const [customEnd, setCustomEnd] = useState(() => ym(new Date()));

  const setPreset = (k) => _setPreset(k);

  const range = useMemo(() => {
    if (preset === "custom") {
      // Tolerate empty / inverted bounds — treat blank as open-ended.
      let s = customStart || null;
      let e = customEnd || null;
      if (s && e && s > e) [s, e] = [e, s];
      return { startYM: s, endYM: e };
    }
    const now = new Date();
    const [startYM, endYM] = (RANGE_PRESETS[preset] || RANGE_PRESETS.all).compute(now);
    return { startYM, endYM };
  }, [preset, customStart, customEnd]);

  const inRange = (m) => {
    if (!m) return false;
    if (range.startYM && m < range.startYM) return false;
    if (range.endYM && m > range.endYM) return false;
    return true;
  };

  const filter = (rows, monthKey = "month") =>
    (rows || []).filter((r) => inRange(r?.[monthKey]));

  return {
    preset, setPreset, range, inRange, filter,
    customStart, customEnd, setCustomStart, setCustomEnd,
  };
}

const PRETTY_MONTH = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

/**
 * <ChartDateRange> — top-right control rendered on top of a chart.
 *
 * Props:
 *  - preset, setPreset, range  from useChartDateRange
 *  - customStart, customEnd, setCustomStart, setCustomEnd (from the hook)
 *  - testIdPrefix              for the data-testid on inputs (default "date-range")
 *  - actualRange [optional]    {startYM, endYM} representing the data's true
 *                              extent. Used for the "All time" label.
 */
export default function ChartDateRange({
  preset, setPreset, range, testIdPrefix = "date-range", actualRange,
  customStart, customEnd, setCustomStart, setCustomEnd,
}) {
  const display = useMemo(() => {
    const s = range.startYM || actualRange?.startYM;
    const e = range.endYM || actualRange?.endYM;
    if (!s && !e) return "no data";
    if (s === e) return PRETTY_MONTH(s);
    return `${PRETTY_MONTH(s)} – ${PRETTY_MONTH(e)}`;
  }, [range, actualRange]);

  const isCustom = preset === "custom";

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-wrap`}>
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-zinc-50 border border-zinc-200
                   text-[11px] font-mono uppercase tracking-wider text-zinc-700"
        data-testid={`${testIdPrefix}-display`}
        title="Active date range"
      >
        <CalendarRange className="h-3 w-3 text-zinc-400" />
        {display}
      </span>
      <Select value={preset} onValueChange={setPreset}>
        <SelectTrigger
          className="rounded-sm h-8 text-xs w-[180px]"
          data-testid={`${testIdPrefix}-select`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(RANGE_PRESETS).map(([k, v]) => (
            <SelectItem
              key={k}
              value={k}
              data-testid={`${testIdPrefix}-option-${k}`}
            >
              {v.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isCustom && setCustomStart && setCustomEnd && (
        <div
          className="inline-flex items-center gap-1.5 pl-2 border-l border-zinc-200"
          data-testid={`${testIdPrefix}-custom-wrap`}
        >
          <input
            type="month"
            value={customStart || ""}
            onChange={(e) => setCustomStart(e.target.value)}
            className="rounded-sm border border-zinc-200 h-8 px-2 text-xs font-mono bg-white"
            title="Custom start month"
            data-testid={`${testIdPrefix}-custom-start`}
          />
          <span className="text-zinc-400 text-xs">→</span>
          <input
            type="month"
            value={customEnd || ""}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="rounded-sm border border-zinc-200 h-8 px-2 text-xs font-mono bg-white"
            title="Custom end month"
            data-testid={`${testIdPrefix}-custom-end`}
          />
        </div>
      )}
    </div>
  );
}
